import { ITextBuilder } from "../../Global/Base/Builders/ITextBuilder";
import { Event } from "../../Global/Base/Events";
import { ValidationErrorType } from "../../OW.Remoting/Basic/EOWExceptionsInterfaces";

/**
 * Rozhraní pro komponenty, které je možné řídit právy
 * @internal
 */
export interface IBaseControlRights {
    /**
     * Právo editace
     */
    enabled: boolean;

    /**
     * Právo viditelnosti
     */
    visible: boolean;
}

export class IBaseControlRights {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "enabled") && Reflect.has(instance, "visible");
    }
}

/**
 * Rozhraní pro editovatelné komponenty, které je možné řídit právy
 * @internal
 */
export interface IBaseEditableControlRights extends IBaseControlRights {
    /**
     * Právo povinnosti
     */
    required: boolean;
}

export class IBaseEditableControlRights {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "required") && instance instanceof IBaseControlRights;
    }
}

export interface IHasValidityState {
    /**
     * Stav validnosti komponenty
     */
    isValid: boolean;
}

export class IHasValidityState {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "isValid");
    }
}

/**
 * Vysledek zmeny validnosti - novy stav validnosti a text pripadne chyby
 */
export interface IValidationResult {
    valid: boolean;
    invalidValue?: ITextBuilder;
    invalidLocKey?: string;
    invalidMessage?: string;
    errorType?: ValidationErrorType;
}

/**
 * Rozhraní pro validovatelné komponenty
 *
 * @generateInstanceClass
 */
export interface IValidatable extends IHasValidityState {
    /**
     * Událost vyvolaná při změně validnosti komponenty
     */
    validityChanged: Event<IValidatable, IValidationResult>;
    /**
     * True, pokud se má v tooltipu zobrazit hláška, že hodnota je povinná, ale není vyplněna (platnou) hodnotou.
     */
    isInRequiredInvalidState: boolean;
    /**
     * Současný stav validnosti.
     */
    readonly currentValidity: IValidationResult;
    /**
     * Komponenta provede svou validaci a podle toho si nastaví svůj stav validnosti (IsValid)
     */
    validate(): boolean;
    /**
     * Komponenta si natvrdo nastaví svůj stav na validní
     */
    setValid(): void;
    /**
     * Komponenta si natvrdo nastaví svůj stav na nevalidní
     * @param locKey klíč validační hlášky, která se má zobrazit
     * @param message nebo už lokalizovaná zpráva, která se má zobrazit
     * @param errorType typ chyby
     */
    setInvalid(locKey: string, message?: string, errorType?: ValidationErrorType): void;
}

export class IValidatable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            typeof instance.validate === "function" &&
            typeof instance.setValid === "function" &&
            typeof instance.setInvalid === "function" &&
            Reflect.has(instance, "validityChanged") &&
            Reflect.has(instance, "isInRequiredInvalidState") &&
            Reflect.has(instance, "currentValidity") &&
            instance instanceof IHasValidityState;
    }
}

export interface IContainer {
    readonly isContainer: boolean; // pomocna property jen kvuli runtime kontrole rozhrani - pokud v budoucnu vznikne nejaka spolecna property (treba children...), tak se to muze nahradit ji
}

export class IContainer {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "isContainer");
    }
}

/**
 * Rozhraní pro validovatelné kontejnerové komponenty
 */
export interface IValidatableContainer extends IContainer, IHasValidityState {
}

export class IValidatableContainer {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance instanceof IContainer && instance instanceof IHasValidityState;
    }
}

/**
 * Rozhraní pro elementy, které si přejí dostávat notifikace o odpojení z DOMu
 */
export interface INotifyDetached {
    /**
     * Voláno při odpojení komponenty z DOMu
     */
    detached(): void;
}

export class INotifyDetached {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.detached === "function";
    }
}

/** Slouzi k nastavovani interactive na needitacnich komponentach */
export interface IInteractiveControl {
    interactive: boolean;
}

export class IInteractiveControl {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "interactive");
    }
}

export interface IEnabledVisualControl {
    enabledVisual: boolean;
}

export class IEnabledVisualControl {
    private static readonly _properties: (keyof IEnabledVisualControl)[] = ["enabledVisual"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some(property => !Reflect.has(instance, property));
    }
}

/**
  * Určuje, zda je aktivnost komponenty řízena i přes vlastnost enableInEdit
  * @includeToDoc
 */
export interface IEnableInEditControl {
    /** Akce je přístupná pouze v edit. módu */
    enableInEdit: boolean;
}

export class IEnableInEditControl {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "enableInEdit");
    }
}

/**
 * Rozhraní pro akce, které mají možnost být spouštěné nehledě na právo editace
 */
export interface IRunWithoutEdit {
    /**
     * Příznak, zda má být možné akci spustit bez ohledu na právo editace
     */
    runWithoutEdit: boolean;
}

export class IRunWithoutEdit {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "runWithoutEdit");
    }
}

/**
 * Rozhraní umožňuje čekat na běžící procesy na komponentě
 * @includeToDoc
 */
export interface IBussyAwaitable {
    /**
     * Umožňuje čekat na běžící procesy na komponentě
     */
    waitForPendingActions(): Promise<void>;
    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    cancelPendingActions(): void;
}

export class IBussyAwaitable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.waitForPendingActions === "function" && typeof instance.cancelPendingActions === "function";
    }
}

/**
 * Rozhraní které umožňuje volat před uložením operace
 * @includeToDoc
 */
export interface IBeforeSaveAwaitable {
    /**
     * Je zavoláno před uložením a je třeba na tuto akci počkat
     */
    beforeSave(): Promise<void>;

}

export class IBeforeSaveAwaitable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.beforeSave === "function";
    }
}

export interface IDataLoadingAwaitable {
    waitOnDataLoaded(): Promise<boolean>;
}

export class IDataLoadingAwaitable {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.waitOnDataLoaded === "function";
    }
}
